import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(
      CartAddEvent.eventName,
      this.#handleCartAdd
    );

    this.addEventListener(
      'click',
      this.#handleCheckoutClick,
      { capture: true }
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(
      CartAddEvent.eventName,
      this.#handleCartAdd
    );

    this.removeEventListener(
      'click',
      this.#handleCheckoutClick,
      { capture: true }
    );
  }

  /**
   * Fallback listener for the checkout button.
   *
   * @param {MouseEvent} event
   */
  #handleCheckoutClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const checkoutButton = event.target.closest(
      '#checkout, button[name="checkout"], .cart__checkout-button'
    );

    if (!checkoutButton) return;
    if (!this.contains(checkoutButton)) return;

    this.closeImmediately();
  };

  #handleCartAdd = () => {
    window.dispatchEvent(
      new Event('tb:cart:updated')
    );

    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  open() {
    this.showDialog();

    window.dispatchEvent(
      new Event('tb:cart:updated')
    );

    /**
     * Close drawer when Shopify installments opens.
     */
    customElements
      .whenDefined('shopify-payment-terms')
      .then(() => {
        const installmentsContent =
          document.querySelector(
            'shopify-payment-terms'
          )?.shadowRoot;

        const cta =
          installmentsContent?.querySelector(
            '#shopify-installments-cta'
          );

        cta?.addEventListener(
          'click',
          this.closeDialog,
          { once: true }
        );
      });
  }

  /**
   * Immediately close the cart drawer before Razorpay opens.
   * This does not stop or cancel the checkout event.
   */
  closeImmediately() {
    const { dialog } = this.refs;

    if (!dialog?.open) return;

    const bodyTop = Number.parseInt(
      document.body.style.top || '0',
      10
    );

    const previousScrollPosition =
      bodyTop < 0
        ? Math.abs(bodyTop)
        : window.scrollY;

    /*
     * Start the normal cleanup process. This removes the
     * dialog's internal click and keyboard listeners.
     */
    void this.closeDialog();

    /*
     * Close synchronously instead of waiting for the
     * drawer's closing animation.
     */
    if (dialog.open) {
      dialog.close();
    }

    dialog.classList.remove('dialog-closing');

    document.body.style.width = '';
    document.body.style.position = '';
    document.body.style.top = '';

    window.scrollTo({
      top: previousScrollPosition,
      behavior: 'instant'
    });
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define(
    'cart-drawer-component',
    CartDrawerComponent
  );
}

/* =========================================================
   TEARBLENDS: Upsell Add button
   Only one handler is used to prevent duplicate additions.
   ========================================================= */

(() => {
  document.addEventListener(
    'click',
    async (event) => {
      if (!(event.target instanceof Element)) return;

      const button = event.target.closest(
        '[data-tb-upsell-add]'
      );

      if (
        !button ||
        !(button instanceof HTMLButtonElement)
      ) {
        return;
      }

      if (button.disabled) return;

      const variantId =
        button.getAttribute('data-variant-id');

      if (!variantId) return;

      const originalText = button.textContent;

      button.disabled = true;
      button.textContent = 'Adding...';

      try {
        const sectionIds = [
          ...new Set(
            Array.from(
              document.querySelectorAll(
                'cart-items-component[data-section-id]'
              )
            )
              .map((component) =>
                component.getAttribute(
                  'data-section-id'
                )
              )
              .filter(Boolean)
          )
        ];

        const requestBody = {
          items: [
            {
              id: Number.parseInt(
                variantId,
                10
              ),
              quantity: 1
            }
          ],
          sections: sectionIds.join(','),
          sections_url:
            window.location.pathname
        };

        const response = await fetch(
          '/cart/add.js',
          {
            method: 'POST',
            headers: {
              'Content-Type':
                'application/json',
              Accept: 'application/json'
            },
            body: JSON.stringify(requestBody)
          }
        );

        const result = await response.json();

        if (!response.ok) {
          throw new Error(
            result.description ||
              result.message ||
              'Add failed'
          );
        }

        document.dispatchEvent(
          new CartAddEvent(
            {},
            variantId,
            {
              source: 'tearblends-upsell',
              itemCount: 1,
              variantId,
              sections:
                result.sections || {}
            }
          )
        );

        window.dispatchEvent(
          new Event('tb:cart:updated')
        );

        button.textContent = 'Added';

        setTimeout(() => {
          button.textContent = originalText;
          button.disabled = false;
        }, 800);
      } catch (error) {
        console.error(
          'TEARBLENDS upsell error:',
          error
        );

        button.textContent = 'Error';

        setTimeout(() => {
          button.textContent = originalText;
          button.disabled = false;
        }, 1200);
      }
    }
  );
})();

/* TÉARBLENDS — Cart reservation countdown */
(() => {
  const TIMER_DURATION = 2 * 60;
  let remainingSeconds = TIMER_DURATION;
  let timerInterval;

  function updateTimerDisplay() {
    const timer = document.getElementById('tbReserveTimer');

    // The timer may temporarily disappear during Shopify cart updates.
    if (!timer) return;

    const minutes = Math.floor(remainingSeconds / 60);
    const seconds = remainingSeconds % 60;

    timer.textContent =
      `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  }

  function startCartTimer() {
    window.clearInterval(timerInterval);

    remainingSeconds = TIMER_DURATION;
    updateTimerDisplay();

    timerInterval = window.setInterval(() => {
      remainingSeconds = Math.max(0, remainingSeconds - 1);
      updateTimerDisplay();

      if (remainingSeconds === 0) {
        window.clearInterval(timerInterval);
      }
    }, 1000);
  }

  // Start when the page loads.
  startCartTimer();

  // Restart whenever the cart drawer opens or the cart changes.
  window.addEventListener('tb:cart:updated', startCartTimer);
})();